import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type {
  Manuscript as ManuscriptType,
  ManuscriptMetadata as ManuscriptMetadataType,
  Evidence,
} from "@/routes/innovation-days-demo/types";
import type { SentenceRefinement, ClaimVerification } from "@/types/summary";
import { UnifiedAnnotation } from "@/routes/innovation-days-demo/components/UnifiedAnnotation";
import { mapClaimsToManuscript } from "@/utils/claimMapping";
import { findAllOccurrences } from "@/utils/textMatching";

type AnnotationType =
  "quote" | "content_flag" | "motif" | "theme" | "trope" | "claims";

interface ManuscriptProps {
  manuscript: ManuscriptType;
  manuscriptMetadata: ManuscriptMetadataType;
  annotationType: AnnotationType | null;
  annotationIndex?: number;
  sentenceRefinement?: SentenceRefinement | null;
}

interface AnnotationWithEvidence {
  type: AnnotationType;
  content: string;
  categories?: string[];
  evidence: Evidence | Evidence[];
  claim?: ClaimVerification; // For claims mode - full claim data
}

interface AnnotationPosition {
  annotationIndex: number;
  top: number;
  paragraphIndex: number;
  height?: number;
}

export function InteractiveViewer({
  manuscript,
  manuscriptMetadata,
  annotationType,
  annotationIndex,
  sentenceRefinement,
}: ManuscriptProps) {
  const [currentAnnotationIndex, setCurrentAnnotationIndex] = useState(
    annotationIndex ?? 0,
  );
  const [shouldScroll, setShouldScroll] = useState(false);
  const [visibleAnnotations, setVisibleAnnotations] = useState<
    AnnotationPosition[]
  >([]);
  const isNavigatingRef = useRef(false);
  const isInteractingRef = useRef(false);
  const manuscriptContainerRef = useRef<HTMLDivElement>(null);
  const annotationRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Get all annotations based on the selected type
  const annotations = useMemo((): AnnotationWithEvidence[] => {
    if (!annotationType) return [];

    switch (annotationType) {
      case "quote": {
        const quoteAnnotations: AnnotationWithEvidence[] = [];
        manuscriptMetadata.quotes.forEach((quoteTopic) => {
          quoteTopic.quotes.forEach((quote) => {
            quoteAnnotations.push({
              type: "quote",
              content: quoteTopic.topic,
              evidence: quote.evidence,
            });
          });
        });
        return quoteAnnotations;
      }
      case "content_flag":
        return manuscriptMetadata.content_flags.map((flag) => ({
          type: "content_flag",
          content: flag.short_description,
          categories: flag.categories,
          evidence: flag.evidence,
        }));
      case "motif":
        return manuscriptMetadata.motifs.map((motif) => ({
          type: "motif",
          content: `${motif.name}: ${motif.description}`,
          evidence: [], // Motifs don't have evidence in the current schema
        }));
      case "theme":
        return manuscriptMetadata.themes
          ? manuscriptMetadata.themes.map((theme) => ({
              type: "theme",
              content: `${theme.short}: ${theme.long}`,
              evidence: theme.evidence || [],
            }))
          : [];
      case "trope":
        return manuscriptMetadata.tropes.map((trope) => ({
          type: "trope",
          content: `${trope.title}: ${trope.description}`,
          evidence: trope.evidence || [],
        }));
      case "claims": {
        if (!sentenceRefinement) return [];

        // Filter to only supported and partially supported claims
        const filteredClaims = sentenceRefinement.claim_verifications.filter(
          (claim) =>
            claim.assessment === "support" ||
            claim.assessment === "partially_support",
        );

        if (filteredClaims.length === 0) return [];

        // Map claims to manuscript paragraphs
        const claimMappings = mapClaimsToManuscript(
          filteredClaims,
          manuscript.paragraphs as string[],
        );

        // Convert to AnnotationWithEvidence format
        return claimMappings.map((mapping) => {
          // Create evidence array from paragraph mappings
          // Note: We use 0 for sentence indices as claims use text-based matching
          const evidence: Evidence[] = Array.from(mapping.paragraphIndices).map(
            (paragraphIndex) => ({
              paragraph_index: paragraphIndex,
              start_sentence_index_within_paragraph: 0,
              end_sentence_index_within_paragraph: 0,
              evidence_sentences:
                mapping.evidenceByParagraph.get(paragraphIndex) || [],
            }),
          );

          return {
            type: "claims",
            content: mapping.claim.claim,
            evidence,
            claim: mapping.claim, // Store full claim data
          };
        });
      }
      default:
        return [];
    }
  }, [
    annotationType,
    manuscriptMetadata,
    sentenceRefinement,
    manuscript.paragraphs,
  ]);

  // Reset current index when annotation type changes
  useEffect(() => {
    setCurrentAnnotationIndex(annotationIndex ?? 0);
  }, [annotationType, annotationIndex]);

  // Scroll to the annotation index when provided via URL
  useEffect(() => {
    if (annotationIndex !== undefined && annotations.length > 0) {
      setShouldScroll(true);
    }
  }, [annotationIndex, annotations.length]);

  // Auto-scroll to first claim when entering claims mode
  useEffect(() => {
    if (annotationType === "claims" && annotations.length > 0) {
      setShouldScroll(true);
    }
  }, [annotationType, annotations.length]);

  // Build paragraph-to-annotations mapping using useMemo
  const highlightedParagraphs = useMemo(() => {
    const paragraphSet = new Set<number>();

    annotations.forEach((annotation) => {
      const evidenceArray = Array.isArray(annotation.evidence)
        ? annotation.evidence
        : [annotation.evidence];
      evidenceArray.forEach((ev) => {
        if (ev && typeof ev.paragraph_index === "number") {
          paragraphSet.add(ev.paragraph_index);
        }
      });
    });

    return paragraphSet;
  }, [annotations]);

  const paragraphToAnnotationsMap = useMemo(() => {
    const paragraphMap = new Map<number, number[]>();

    annotations.forEach((annotation, annotationIdx) => {
      const evidenceArray = Array.isArray(annotation.evidence)
        ? annotation.evidence
        : [annotation.evidence];
      evidenceArray.forEach((ev) => {
        if (ev && typeof ev.paragraph_index === "number") {
          // Map each paragraph to an array of annotation indices
          const existing = paragraphMap.get(ev.paragraph_index) || [];
          if (!existing.includes(annotationIdx)) {
            paragraphMap.set(ev.paragraph_index, [...existing, annotationIdx]);
          }
        }
      });
    });

    return paragraphMap;
  }, [annotations]);

  const paragraphToEvidenceSentences = useMemo(() => {
    const evidenceSentencesMap = new Map<number, string[]>();

    annotations.forEach((annotation) => {
      const evidenceArray = Array.isArray(annotation.evidence)
        ? annotation.evidence
        : [annotation.evidence];
      evidenceArray.forEach((ev) => {
        if (ev && typeof ev.paragraph_index === "number") {
          // Store evidence sentences for this paragraph
          if (ev.evidence_sentences && ev.evidence_sentences.length > 0) {
            const existingSentences =
              evidenceSentencesMap.get(ev.paragraph_index) || [];
            evidenceSentencesMap.set(ev.paragraph_index, [
              ...existingSentences,
              ...ev.evidence_sentences,
            ]);
          }
        }
      });
    });

    return evidenceSentencesMap;
  }, [annotations]);

  // Scroll to current annotation only when navigation buttons are clicked
  useEffect(() => {
    if (!shouldScroll) return;

    if (
      annotations.length === 0 ||
      currentAnnotationIndex >= annotations.length
    ) {
      setShouldScroll(false);
      return;
    }
    const currentAnnotation = annotations[currentAnnotationIndex];
    const evidenceArray = Array.isArray(currentAnnotation.evidence)
      ? currentAnnotation.evidence
      : [currentAnnotation.evidence];
    const firstEvidence = evidenceArray.find(
      (ev) => ev && typeof ev.paragraph_index === "number",
    );
    const paragraphIndex = firstEvidence?.paragraph_index;

    if (paragraphIndex !== null && paragraphIndex !== undefined) {
      const element = document.querySelector(
        `[data-paragraph-index="${paragraphIndex}"]`,
      );
      if (element) {
        // Set navigation flag BEFORE scrolling
        isNavigatingRef.current = true;
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        // Reset navigation flag after scroll completes (increased timeout)
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 1500);
      }
    }

    setShouldScroll(false);
  }, [currentAnnotationIndex, annotations, shouldScroll]);

  // Update annotation positions based on their corresponding paragraphs
  const updateVisibleAnnotations = useCallback(() => {
    if (annotations.length === 0 || !manuscriptContainerRef.current) {
      setVisibleAnnotations([]);
      return;
    }

    const containerElement = manuscriptContainerRef.current;
    const containerRect = containerElement.getBoundingClientRect();
    const annotationPositions: AnnotationPosition[] = [];
    const seenAnnotations = new Set<number>();

    // Get positions for ALL highlighted paragraphs
    // Sort paragraph indices to ensure we process them in order and position each annotation at its FIRST paragraph
    const sortedParagraphIndices = Array.from(highlightedParagraphs).sort(
      (a, b) => a - b,
    );

    sortedParagraphIndices.forEach((paragraphIndex) => {
      const paragraphElement = document.querySelector(
        `[data-paragraph-index="${paragraphIndex}"]`,
      );

      if (paragraphElement) {
        const paragraphRect = paragraphElement.getBoundingClientRect();
        const annotationIndices =
          paragraphToAnnotationsMap.get(paragraphIndex) || [];

        annotationIndices.forEach((annotationIdx) => {
          // Only add each annotation once (use first paragraph)
          if (!seenAnnotations.has(annotationIdx)) {
            seenAnnotations.add(annotationIdx);
            annotationPositions.push({
              annotationIndex: annotationIdx,
              top: paragraphRect.top - containerRect.top,
              paragraphIndex,
            });
          }
        });
      }
    });

    // Sort by top position
    annotationPositions.sort((a, b) => a.top - b.top);

    if (annotationPositions.length <= 1) {
      setVisibleAnnotations(annotationPositions);
      return;
    }

    // Constants for overlap prevention
    const SPACING = 12;
    const DEFAULT_HEIGHT = 350;
    const containerHeight = containerElement.offsetHeight;

    // Helper to get annotation height
    const getHeight = (pos: AnnotationPosition) => {
      const key = `annotation-${pos.annotationIndex}-${pos.paragraphIndex}`;
      const element = annotationRefs.current.get(key);
      return element?.offsetHeight || DEFAULT_HEIGHT;
    };

    // Helper to prevent overlap between adjacent cards
    const adjustPosition = (
      current: AnnotationPosition,
      adjacent: AnnotationPosition,
      adjacentHeight: number,
      isAbove: boolean,
    ): number => {
      const currentHeight = getHeight(current);

      if (isAbove) {
        // Card is above adjacent - ensure it doesn't overlap
        const maxTop = adjacent.top - currentHeight - SPACING;
        return Math.min(current.top, maxTop);
      }
      // Card is below adjacent - ensure it doesn't overlap
      const minTopNoOverlap = adjacent.top + adjacentHeight + SPACING;
      const maxTopForCurrent = Math.max(0, containerHeight - currentHeight);

      // Prioritize no overlap over staying within bounds
      if (minTopNoOverlap > maxTopForCurrent) {
        return Math.max(minTopNoOverlap, current.top);
      }
      return Math.max(current.top, Math.min(minTopNoOverlap, maxTopForCurrent));
    };

    // Find active annotation index
    const activeIndex = annotationPositions.findIndex(
      (pos) => pos.annotationIndex === currentAnnotationIndex,
    );

    // Bound active card first if it exists
    if (activeIndex !== -1) {
      const activePos = annotationPositions[activeIndex];
      const activeHeight = getHeight(activePos);
      const maxTop = Math.max(0, containerHeight - activeHeight);

      activePos.top = Math.max(0, Math.min(activePos.top, maxTop));

      // Adjust cards above active card (work backwards)
      for (let i = activeIndex - 1; i >= 0; i -= 1) {
        annotationPositions[i].top = adjustPosition(
          annotationPositions[i],
          annotationPositions[i + 1],
          getHeight(annotationPositions[i + 1]),
          true,
        );
      }

      // Adjust cards below active card (work forwards)
      for (let i = activeIndex + 1; i < annotationPositions.length; i += 1) {
        annotationPositions[i].top = adjustPosition(
          annotationPositions[i],
          annotationPositions[i - 1],
          getHeight(annotationPositions[i - 1]),
          false,
        );
      }
    } else {
      // No active card - adjust all cards sequentially
      for (let i = 1; i < annotationPositions.length; i += 1) {
        annotationPositions[i].top = adjustPosition(
          annotationPositions[i],
          annotationPositions[i - 1],
          getHeight(annotationPositions[i - 1]),
          false,
        );
      }
    }

    setVisibleAnnotations(annotationPositions);
  }, [
    annotations.length,
    highlightedParagraphs,
    paragraphToAnnotationsMap,
    currentAnnotationIndex,
  ]);

  // Callback to trigger position recalculation (used for expand/collapse)
  const handleCardInteraction = useCallback(() => {
    // Set interaction flag to prevent mobile auto-select during card expansion
    isInteractingRef.current = true;

    // Small delay to allow DOM to update before recalculating
    setTimeout(() => {
      updateVisibleAnnotations();
      // Reset interaction flag after update completes
      setTimeout(() => {
        isInteractingRef.current = false;
      }, 100);
    }, 50);
  }, [updateVisibleAnnotations]);

  // Update annotation positions on scroll and when dependencies change
  useEffect(() => {
    // Initial calculation
    updateVisibleAnnotations();

    // Recalculate after a short delay to ensure refs are populated and DOM is settled
    const initialTimeout = setTimeout(() => {
      updateVisibleAnnotations();
    }, 100);

    const handleScroll = () => {
      if (!isNavigatingRef.current) {
        updateVisibleAnnotations();
      }
    };

    const handleResize = () => {
      updateVisibleAnnotations();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateVisibleAnnotations]);

  // Auto-select annotation for mobile view based on paragraph in top 50% of viewport
  // This runs whenever visibleAnnotations or scroll position changes
  useEffect(() => {
    const updateMobileActiveAnnotation = () => {
      // Only run on mobile (below md breakpoint - 768px)
      if (window.innerWidth >= 1024) {
        return;
      }

      // Skip for claims mode - disable auto-selection on scroll
      if (annotationType === "claims") {
        return;
      }

      // Skip if user is actively interacting with cards (expanding/collapsing)
      if (isInteractingRef.current) {
        return;
      }

      const viewportHeight = window.innerHeight;

      // Find annotations whose paragraphs are in the top 50% of viewport
      const filtered = visibleAnnotations.filter((annotationPos) => {
        const paragraphElement = document.querySelector(
          `[data-paragraph-index="${annotationPos.paragraphIndex}"]`,
        );

        if (!paragraphElement) return false;

        const paragraphRect = paragraphElement.getBoundingClientRect();

        // Only consider if paragraph is in the top 50% of the viewport
        return (
          paragraphRect.top >= 0 && paragraphRect.top < viewportHeight * 0.5
        );
      });

      // Auto-select the latest (bottom-most) annotation in the top 50% of viewport
      if (filtered.length > 0 && !isNavigatingRef.current) {
        const latestAnnotation = filtered[filtered.length - 1];
        setCurrentAnnotationIndex(latestAnnotation.annotationIndex);
      }
    };

    updateMobileActiveAnnotation();

    const handleScroll = () => {
      if (!isNavigatingRef.current) {
        updateMobileActiveAnnotation();
      }
    };

    const handleResize = () => {
      updateMobileActiveAnnotation();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [visibleAnnotations, annotationType]);

  // Check if a paragraph is a chapter heading
  const isChapterHeading = (text: string): boolean => {
    return /^(CHAPTER|Chapter)\s+\d+/.test(text.trim());
  };

  // Get the annotation index for a sentence in this paragraph
  // Returns the first matching annotation index, or null if not found
  const getAnnotationIndexForSentence = (
    paragraphIndex: number,
    sentence: string,
  ): number | null => {
    const annotationIndices =
      paragraphToAnnotationsMap.get(paragraphIndex) || [];

    // Find which annotation(s) contain this sentence
    const foundAnnotationIdx = annotationIndices.find((annotationIdx) => {
      const annotation = annotations[annotationIdx];
      const evidenceArray = Array.isArray(annotation.evidence)
        ? annotation.evidence
        : [annotation.evidence];

      return evidenceArray.some(
        (ev) =>
          ev &&
          ev.paragraph_index === paragraphIndex &&
          ev.evidence_sentences?.includes(sentence),
      );
    });

    return foundAnnotationIdx !== undefined ? foundAnnotationIdx : null;
  };

  // Render a paragraph with sentence-level highlighting
  const renderParagraphWithHighlights = (
    paragraphText: string,
    paragraphIndex: number,
  ) => {
    // If no annotation type is selected, just return plain text
    if (!annotationType) {
      return paragraphText;
    }

    // Special handling for claims mode - use text matching
    if (annotationType === "claims") {
      const currentAnnotation = annotations[currentAnnotationIndex];
      if (!currentAnnotation || !currentAnnotation.claim) {
        return paragraphText;
      }

      // Find all occurrences of evidence in this paragraph
      const highlightPositions: Array<{ start: number; end: number }> = [];
      const evidenceArray = Array.isArray(currentAnnotation.evidence)
        ? currentAnnotation.evidence
        : [currentAnnotation.evidence];

      evidenceArray.forEach((ev) => {
        if (
          ev &&
          ev.paragraph_index === paragraphIndex &&
          ev.evidence_sentences
        ) {
          ev.evidence_sentences.forEach((evidenceText) => {
            const positions = findAllOccurrences(paragraphText, evidenceText);
            highlightPositions.push(...positions);
          });
        }
      });

      if (highlightPositions.length === 0) {
        return paragraphText;
      }

      // Sort and merge overlapping positions
      highlightPositions.sort((a, b) => a.start - b.start);
      const mergedPositions: Array<{ start: number; end: number }> = [];
      highlightPositions.forEach((pos) => {
        if (
          mergedPositions.length === 0 ||
          pos.start > mergedPositions[mergedPositions.length - 1].end
        ) {
          mergedPositions.push({ ...pos });
        } else {
          mergedPositions[mergedPositions.length - 1].end = Math.max(
            mergedPositions[mergedPositions.length - 1].end,
            pos.end,
          );
        }
      });

      // Get color based on assessment
      const getHighlightColor = () => {
        if (currentAnnotation.claim?.assessment === "support") {
          return "bg-primary-50";
        }
        if (currentAnnotation.claim?.assessment === "partially_support") {
          return "bg-[#FDF5C0]";
        }
        return "bg-secondary-50";
      };

      // Build segments
      const parts: React.JSX.Element[] = [];
      let currentPos = 0;
      mergedPositions.forEach((pos) => {
        // Add non-highlighted text before
        if (pos.start > currentPos) {
          parts.push(
            <span key={`p${paragraphIndex}-text-${pos.start}`}>
              {paragraphText.slice(currentPos, pos.start)}
            </span>,
          );
        }
        // Add highlighted text
        parts.push(
          <span
            key={`p${paragraphIndex}-highlight-${pos.start}-${pos.end}`}
            className={`${getHighlightColor()} cursor-pointer transition-colors duration-200`}
            onClick={() => {
              setCurrentAnnotationIndex(currentAnnotationIndex);
              setShouldScroll(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setCurrentAnnotationIndex(currentAnnotationIndex);
                setShouldScroll(true);
              }
            }}
            role="button"
            tabIndex={0}
          >
            {paragraphText.slice(pos.start, pos.end)}
          </span>,
        );
        currentPos = pos.end;
      });

      // Add remaining text
      if (currentPos < paragraphText.length) {
        parts.push(
          <span key={`p${paragraphIndex}-text-final`}>
            {paragraphText.slice(currentPos)}
          </span>,
        );
      }

      return parts;
    }

    // Original logic for other annotation types
    const evidenceSentences = paragraphToEvidenceSentences.get(paragraphIndex);

    if (!evidenceSentences || evidenceSentences.length === 0) {
      // No highlights for this paragraph
      return paragraphText;
    }

    // Find and highlight each evidence sentence within the paragraph
    const parts: React.JSX.Element[] = [];
    let currentPosition = 0;
    let keyCounter = 0;

    // Sort evidence sentences by their position in the paragraph
    const sortedSentences = [...evidenceSentences].sort((a, b) => {
      const posA = paragraphText.indexOf(a, currentPosition);
      const posB = paragraphText.indexOf(b, currentPosition);
      return posA - posB;
    });

    sortedSentences.forEach((evidenceSentence) => {
      const sentenceStart = paragraphText.indexOf(
        evidenceSentence,
        currentPosition,
      );

      if (sentenceStart !== -1) {
        // Add non-highlighted text before this evidence sentence
        if (sentenceStart > currentPosition) {
          const beforeText = paragraphText.slice(
            currentPosition,
            sentenceStart,
          );
          keyCounter += 1;
          parts.push(
            <span key={`p${paragraphIndex}-normal-${keyCounter}`}>
              {beforeText}
            </span>,
          );
        }

        // Get annotation index for this sentence
        const sentenceAnnotationIdx = getAnnotationIndexForSentence(
          paragraphIndex,
          evidenceSentence,
        );
        const isActive = sentenceAnnotationIdx === currentAnnotationIndex;

        // Add highlighted evidence sentence with conditional styling
        keyCounter += 1;
        parts.push(
          <span
            key={`p${paragraphIndex}-highlight-${keyCounter}`}
            className={`cursor-pointer transition-colors duration-200 ${
              isActive ? "bg-[#FDF5C0]" : "bg-[#ECF0F5]"
            } hover:bg-[#FDF5C0]`}
            onClick={() => {
              if (sentenceAnnotationIdx !== null) {
                setCurrentAnnotationIndex(sentenceAnnotationIdx);
                setShouldScroll(true);
              }
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (
                (e.key === "Enter" || e.key === " ") &&
                sentenceAnnotationIdx !== null
              ) {
                e.preventDefault();
                setCurrentAnnotationIndex(sentenceAnnotationIdx);
                setShouldScroll(true);
              }
            }}
          >
            {evidenceSentence}
          </span>,
        );

        currentPosition = sentenceStart + evidenceSentence.length;
      }
    });

    // Add any remaining text after the last evidence sentence
    if (currentPosition < paragraphText.length) {
      const remainingText = paragraphText.slice(currentPosition);
      keyCounter += 1;
      parts.push(
        <span key={`p${paragraphIndex}-normal-${keyCounter}`}>
          {remainingText}
        </span>,
      );
    }

    return parts;
  };

  return (
    <div className="min-h-screen pb-8 text-[14px]">
      {/* Main Content - Two column layout on desktop when annotationType exists, single column otherwise */}
      <div
        ref={manuscriptContainerRef}
        className={`grid grid-cols-1 gap-8 relative ${
          annotationType ? "lg:grid-cols-4" : ""
        }`}
      >
        {/* Manuscript Text - Takes 2 columns on desktop when annotations exist, full width otherwise */}
        <div className={annotationType ? "lg:col-span-3" : ""}>
          <div className="bg-white rounded-3xl border shadow-sm p-6 pb-[300px] md:pb-0">
            {manuscript.paragraphs.map((paragraph, index) => {
              const isChapter = isChapterHeading(paragraph as string);

              // Create a unique key using first 50 chars of paragraph + index
              const paragraphKey = `${index}-${(paragraph as string).slice(0, 50)}`;

              return (
                <div
                  key={paragraphKey}
                  data-paragraph-index={index}
                  className="mb-4 transition-all duration-200"
                >
                  {isChapter ? (
                    <h2
                      className="font-semibold text-gray-900 mt-6 mb-4"
                      style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                      }}
                    >
                      {paragraph}
                    </h2>
                  ) : (
                    <p
                      className="text-[15px] leading-[24px] text-justify"
                      style={{
                        color: "#1E293B",
                        fontFamily: 'Georgia, "Times New Roman", serif',
                      }}
                    >
                      {renderParagraphWithHighlights(
                        paragraph as string,
                        index,
                      )}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Annotations - Desktop: Multiple boxes positioned to align with paragraphs */}
        {annotationType && (
          <div className="hidden lg:block lg:col-span-1 relative">
            {visibleAnnotations.map((annotationPos, visibleIndex) => {
              const key = `annotation-${annotationPos.annotationIndex}-${annotationPos.paragraphIndex}`;
              const isActive =
                annotationPos.annotationIndex === currentAnnotationIndex;
              const currentAnnotation =
                annotations[annotationPos.annotationIndex];

              // Check if card is completely out of view at the top
              const cardElement = annotationRefs.current.get(key);
              const cardHeight = cardElement?.offsetHeight || 0;
              const isOutOfViewAbove = annotationPos.top + cardHeight < 0;

              return (
                <div
                  key={key}
                  ref={(el) => {
                    if (el) {
                      annotationRefs.current.set(key, el);
                    } else {
                      annotationRefs.current.delete(key);
                    }
                  }}
                  className="absolute left-0 right-0 transition-all duration-200 mb-4"
                  style={{
                    top: `${annotationPos.top}px`,
                    opacity: isOutOfViewAbove ? 0 : 1,
                  }}
                >
                  {annotationType === "claims" && currentAnnotation.claim ? (
                    <UnifiedAnnotation
                      type="claim"
                      claim={currentAnnotation.claim}
                      currentIndex={visibleIndex}
                      totalCount={visibleAnnotations.length}
                      isSelected={isActive}
                      onSelect={() => {
                        setCurrentAnnotationIndex(
                          annotationPos.annotationIndex,
                        );
                        setShouldScroll(true);
                      }}
                      onNavigateNext={() => {
                        const nextVisibleIndex = visibleIndex + 1;
                        if (nextVisibleIndex < visibleAnnotations.length) {
                          setCurrentAnnotationIndex(
                            visibleAnnotations[nextVisibleIndex]
                              .annotationIndex,
                          );
                          setShouldScroll(true);
                        }
                      }}
                      onNavigatePrevious={() => {
                        const prevVisibleIndex = visibleIndex - 1;
                        if (prevVisibleIndex >= 0) {
                          setCurrentAnnotationIndex(
                            visibleAnnotations[prevVisibleIndex]
                              .annotationIndex,
                          );
                          setShouldScroll(true);
                        }
                      }}
                      onExpandToggle={handleCardInteraction}
                    />
                  ) : (
                    <UnifiedAnnotation
                      type={
                        currentAnnotation.type as Exclude<
                          AnnotationType,
                          "claims"
                        >
                      }
                      content={currentAnnotation.content}
                      categories={currentAnnotation.categories}
                      currentIndex={visibleIndex}
                      totalCount={visibleAnnotations.length}
                      isSelected={isActive}
                      onSelect={() => {
                        setCurrentAnnotationIndex(
                          annotationPos.annotationIndex,
                        );
                        setShouldScroll(true);
                      }}
                      onNavigateNext={() => {
                        if (isActive) {
                          const nextVisibleIndex = visibleIndex + 1;
                          if (nextVisibleIndex < visibleAnnotations.length) {
                            setCurrentAnnotationIndex(
                              visibleAnnotations[nextVisibleIndex]
                                .annotationIndex,
                            );
                            setShouldScroll(true);
                          }
                        } else {
                          setCurrentAnnotationIndex(
                            annotationPos.annotationIndex,
                          );
                          setShouldScroll(true);
                        }
                      }}
                      onNavigatePrevious={() => {
                        if (isActive) {
                          const prevVisibleIndex = visibleIndex - 1;
                          if (prevVisibleIndex >= 0) {
                            setCurrentAnnotationIndex(
                              visibleAnnotations[prevVisibleIndex]
                                .annotationIndex,
                            );
                            setShouldScroll(true);
                          }
                        } else {
                          setCurrentAnnotationIndex(
                            annotationPos.annotationIndex,
                          );
                          setShouldScroll(true);
                        }
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile Bottom Sheet - Shows only the active annotation */}
      {annotationType &&
        annotations.length > 0 &&
        annotations[currentAnnotationIndex] &&
        (() => {
          // Find the current annotation's position in visibleAnnotations
          const currentVisibleIndex = visibleAnnotations.findIndex(
            (pos) => pos.annotationIndex === currentAnnotationIndex,
          );

          return (
            <div className="lg:hidden fixed bottom-0 left-3 right-3 z-50 bg-white rounded-t-3xl shadow-2xl border-2 border-primary-200 max-h-[60vh] overflow-y-auto transition-transform duration-300">
              {annotationType === "claims" &&
              annotations[currentAnnotationIndex].claim ? (
                <UnifiedAnnotation
                  type="claim"
                  claim={annotations[currentAnnotationIndex].claim}
                  currentIndex={
                    currentVisibleIndex !== -1
                      ? currentVisibleIndex
                      : currentAnnotationIndex
                  }
                  totalCount={
                    visibleAnnotations.length > 0
                      ? visibleAnnotations.length
                      : annotations.length
                  }
                  isSelected
                  onSelect={() => {}}
                  onNavigateNext={() => {
                    const nextVisibleIndex = currentVisibleIndex + 1;
                    if (nextVisibleIndex < visibleAnnotations.length) {
                      setCurrentAnnotationIndex(
                        visibleAnnotations[nextVisibleIndex].annotationIndex,
                      );
                      setShouldScroll(true);
                    }
                  }}
                  onNavigatePrevious={() => {
                    const prevVisibleIndex = currentVisibleIndex - 1;
                    if (prevVisibleIndex >= 0) {
                      setCurrentAnnotationIndex(
                        visibleAnnotations[prevVisibleIndex].annotationIndex,
                      );
                      setShouldScroll(true);
                    }
                  }}
                  onExpandToggle={handleCardInteraction}
                  isMobile
                />
              ) : (
                <UnifiedAnnotation
                  type={
                    annotations[currentAnnotationIndex].type as Exclude<
                      AnnotationType,
                      "claims"
                    >
                  }
                  content={annotations[currentAnnotationIndex].content}
                  categories={annotations[currentAnnotationIndex].categories}
                  currentIndex={
                    currentVisibleIndex !== -1
                      ? currentVisibleIndex
                      : currentAnnotationIndex
                  }
                  totalCount={
                    visibleAnnotations.length > 0
                      ? visibleAnnotations.length
                      : annotations.length
                  }
                  isSelected
                  onSelect={() => {}}
                  onNavigateNext={() => {
                    const nextVisibleIndex = currentVisibleIndex + 1;
                    if (nextVisibleIndex < visibleAnnotations.length) {
                      setCurrentAnnotationIndex(
                        visibleAnnotations[nextVisibleIndex].annotationIndex,
                      );
                      setShouldScroll(true);
                    }
                  }}
                  onNavigatePrevious={() => {
                    const prevVisibleIndex = currentVisibleIndex - 1;
                    if (prevVisibleIndex >= 0) {
                      setCurrentAnnotationIndex(
                        visibleAnnotations[prevVisibleIndex].annotationIndex,
                      );
                      setShouldScroll(true);
                    }
                  }}
                  isMobile
                />
              )}
            </div>
          );
        })()}
    </div>
  );
}
